import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Save, Upload, ExternalLink, Unplug, Palette, Share2, Building, ShieldCheck, Image, RefreshCw, AlertTriangle, CheckCircle2, Calendar, Loader2, ChevronUp, ChevronDown as ChevronDownIcon, Globe, Plus, Trash2, GripVertical, X, Star, Users } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { useSettings } from "@/hooks/useSettings";
import IntegrationsTab from "@/components/settings/IntegrationsTab";
import WebsiteTab from "@/components/settings/WebsiteTab";

function ColorPicker({ label, value, onChange }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-2 mt-1">
        <input type="color" value={value || '#000000'} onChange={e => onChange(e.target.value)} className="w-10 h-9 rounded-lg cursor-pointer border border-border" />
        <Input value={value || ''} onChange={e => onChange(e.target.value)} className="rounded-xl font-mono text-xs" placeholder="#000000" />
      </div>
    </div>
  );
}

function LogoUpload({ label, currentUrl, fieldName, onUploaded }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('field', fieldName);
      const res = await axios.post(`${API}/settings/upload-logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) {
        onUploaded(res.data.url);
        toast.success(`${label} uploaded`);
      }
    } catch (e) { toast.error('Upload failed'); }
    setUploading(false);
    if (ref.current) ref.current.value = '';
  };

  return (
    <div>
      <Label className="text-xs mb-2 block">{label}</Label>
      <div className="flex items-center gap-3">
        {currentUrl ? (
          <img src={currentUrl} alt={label} className="w-16 h-16 rounded-xl object-contain border border-border bg-white" />
        ) : (
          <div className="w-16 h-16 rounded-xl border-2 border-dashed border-border flex items-center justify-center text-muted-foreground">
            <Image size={20} />
          </div>
        )}
        <div>
          <Button variant="outline" size="sm" onClick={() => ref.current?.click()} disabled={uploading} className="rounded-xl text-xs">
            <Upload size={12} className="mr-1" /> {uploading ? 'Uploading...' : currentUrl ? 'Replace' : 'Upload'}
          </Button>
          <input ref={ref} type="file" accept=".png,.jpg,.jpeg,.svg,.webp" onChange={handleUpload} className="hidden" />
          <p className="text-[10px] text-muted-foreground mt-1">PNG, JPG or SVG</p>
        </div>
      </div>
    </div>
  );
}

// ── Nail Length Row (master pricing list) ─────────────────────────────────
function NailLengthRow({ entry, index, onChange, onDelete, onPhotoUpload }) {  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { await onPhotoUpload(index, file); } catch {}
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="flex items-center gap-3 p-3 rounded-xl border bg-muted/10">
      {/* Photo thumbnail */}
      <div className="relative shrink-0 cursor-pointer" onClick={() => fileRef.current?.click()}>
        {entry.photo_url ? (
          <img src={entry.photo_url} alt={entry.name} className="w-14 h-14 rounded-xl object-cover border border-border" />
        ) : (
          <div className="w-14 h-14 rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center text-muted-foreground hover:bg-muted/30 transition-colors">
            <Image size={16} />
            <span className="text-[9px] mt-0.5">Photo</span>
          </div>
        )}
        {entry.photo_url && (
          <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center shadow-sm">
            {uploading ? <Loader2 size={9} className="animate-spin" /> : <Upload size={9} />}
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
      </div>

      {/* Name */}
      <Input
        value={entry.name}
        onChange={e => onChange({ ...entry, name: e.target.value })}
        placeholder="e.g. Short"
        className="rounded-xl text-xs h-9 flex-1"
      />

      {/* Price uplift */}
      <div className="flex items-center gap-1 shrink-0">
        <span className="text-xs text-muted-foreground">+£</span>
        <Input
          type="number" step="0.5" min="0"
          value={entry.price_uplift}
          onChange={e => onChange({ ...entry, price_uplift: parseFloat(e.target.value) || 0 })}
          className="rounded-xl text-xs h-9 w-20"
        />
      </div>

      {/* Delete */}
      <Button variant="ghost" size="sm" onClick={onDelete} className="h-9 w-9 p-0 text-destructive shrink-0">
        <Trash2 size={13} />
      </Button>
    </div>
  );
}


// ── Nail Finish / Add-on Row (master list) ────────────────────────────────
function NailFinishRow({ entry, index, onChange, onDelete, onPhotoUpload }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { await onPhotoUpload(index, file); } catch {}
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className={`flex items-start gap-3 p-3 rounded-xl border bg-muted/10 ${!entry.active ? 'opacity-60' : ''}`}>
      {/* Icon thumbnail */}
      <div className="relative shrink-0 cursor-pointer mt-1" onClick={() => fileRef.current?.click()}>
        {entry.icon_url ? (
          <img src={entry.icon_url} alt={entry.name} className="w-14 h-14 rounded-xl object-cover border border-border" />
        ) : (
          <div className="w-14 h-14 rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center text-muted-foreground hover:bg-muted/30 transition-colors">
            <Image size={16} />
            <span className="text-[9px] mt-0.5">Icon</span>
          </div>
        )}
        {entry.icon_url && (
          <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center shadow-sm">
            {uploading ? <Loader2 size={9} className="animate-spin" /> : <Upload size={9} />}
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
      </div>

      {/* Fields */}
      <div className="flex-1 grid grid-cols-1 md:grid-cols-[1fr_2fr_auto_auto_auto] gap-2 items-center">
        <Input
          value={entry.name}
          onChange={e => onChange({ ...entry, name: e.target.value })}
          placeholder="e.g. French"
          className="rounded-xl text-xs h-9"
          data-testid={`finish-name-${index}`}
        />
        <Input
          value={entry.description || ''}
          onChange={e => onChange({ ...entry, description: e.target.value })}
          placeholder="Short description (shown to customers)"
          className="rounded-xl text-xs h-9"
          data-testid={`finish-desc-${index}`}
        />
        <div className="flex items-center gap-1">
          <span className="text-xs text-muted-foreground">+£</span>
          <Input
            type="number" step="0.5" min="0"
            value={entry.price ?? 0}
            onChange={e => onChange({ ...entry, price: parseFloat(e.target.value) || 0 })}
            className="rounded-xl text-xs h-9 w-20"
            data-testid={`finish-price-${index}`}
          />
        </div>
        <label className="flex items-center gap-2 text-[11px] text-muted-foreground cursor-pointer whitespace-nowrap">
          <Switch
            checked={entry.active !== false}
            onCheckedChange={v => onChange({ ...entry, active: v })}
            data-testid={`finish-active-${index}`}
          />
          {entry.active === false ? 'Off' : 'Active'}
        </label>
        <Button variant="ghost" size="sm" onClick={onDelete} className="h-9 w-9 p-0 text-destructive shrink-0" data-testid={`finish-delete-${index}`}>
          <Trash2 size={13} />
        </Button>
      </div>
    </div>
  );
}


// ── Category order editor ──────────────────────────────────────────────────
function CategoryOrderEditor({ value, onChange }) {
  const move = (idx, dir) => {
    const arr = [...value];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= arr.length) return;
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    onChange(arr);
  };
  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  if (value.length === 0) {
    return (
      <p className="text-xs text-muted-foreground italic">
        No custom order set — categories appear in the order they were created.
        Go to <strong>Services</strong> and add a category, then come back here to reorder.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {value.map((cat, i) => (
        <div key={cat} className="flex items-center gap-2 p-2.5 rounded-xl border bg-muted/20">
          <span className="text-xs text-muted-foreground tabular-nums w-5">{i + 1}.</span>
          <span className="flex-1 text-sm font-medium">{cat}</span>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" className="h-6 w-6 rounded-lg" onClick={() => move(i, -1)} disabled={i === 0}><ChevronUp size={12} /></Button>
            <Button variant="ghost" size="icon" className="h-6 w-6 rounded-lg" onClick={() => move(i, 1)} disabled={i === value.length - 1}><ChevronDownIcon size={12} /></Button>
          </div>
        </div>
      ))}
      <p className="text-[10px] text-muted-foreground pt-1">Click Save Changes at the top to apply this order to the booking page.</p>
    </div>
  );
}

export default function Settings() {
  const [settings, setSettings] = useState({});
  const [gcalStatus, setGcalStatus] = useState({ connected: false });
  const [gcalTest, setGcalTest] = useState(null);      // live test result
  const [gcalTesting, setGcalTesting] = useState(false);
  const [gcalCalendars, setGcalCalendars] = useState([]);
  const [syncCalendarId, setSyncCalendarId] = useState('primary');
  const [busyCalendarIds, setBusyCalendarIds] = useState(['primary']);
  const [savingCalSettings, setSavingCalSettings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState([]);
  const { refresh: refreshGlobalSettings } = useSettings();

  useEffect(() => {
    const load = async () => {
      try {
        const [setRes, gcalRes, svcRes] = await Promise.all([
          axios.get(`${API}/settings`),
          axios.get(`${API}/oauth/google/status`),
          axios.get(`${API}/services`)
        ]);
        const s = setRes.data;
        setSettings(s);
        setGcalStatus(gcalRes.data);
        setServices(svcRes.data || []);
        if (s.gcal_sync_calendar_id) setSyncCalendarId(s.gcal_sync_calendar_id);
        if (s.gcal_busy_calendar_ids) setBusyCalendarIds(s.gcal_busy_calendar_ids);
        // Auto-populate category_order from services if not set yet
        if (!s.category_order || s.category_order.length === 0) {
          const cats = [...new Set((svcRes.data || []).map(sv => sv.category || 'Other'))];
          if (cats.length > 0) setSettings(prev => ({ ...prev, category_order: cats }));
        }
      } catch (e) {  }
    };
    load();
  }, []);

  const testGoogleConnection = async () => {
    setGcalTesting(true);
    setGcalTest(null);
    try {
      const res = await axios.get(`${API}/oauth/google/test`);
      setGcalTest(res.data);
      if (res.data.valid) {
        setGcalStatus(s => ({ ...s, connected: true, token_invalid: false }));
        setGcalCalendars(res.data.calendars || []);
        toast.success('Google Calendar connection verified!');
      } else {
        setGcalStatus(s => ({ ...s, token_invalid: true }));
        toast.error(res.data.error || 'Connection failed — please reconnect');
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Test failed — check your connection';
      toast.error(msg);
      setGcalTest({ valid: false, error: msg });
    }
    setGcalTesting(false);
  };

  const loadCalendars = async () => {
    try {
      const res = await axios.get(`${API}/calendar/list`);
      if (res.data.calendars?.length) {
        setGcalCalendars(res.data.calendars);
      }
    } catch (e) {  }
  };

  const saveCalendarSettings = async () => {
    setSavingCalSettings(true);
    try {
      await axios.post(`${API}/calendar/settings`, {
        sync_calendar_id: syncCalendarId,
        busy_calendar_ids: busyCalendarIds
      });
      toast.success('Calendar settings saved!');
    } catch (e) { toast.error('Failed to save calendar settings'); }
    setSavingCalSettings(false);
  };

  const toggleBusyCalendar = (calId) => {
    setBusyCalendarIds(prev =>
      prev.includes(calId) ? prev.filter(id => id !== calId) : [...prev, calId]
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings`, settings);
      setSettings(res.data);
      await refreshGlobalSettings(); // Apply theme globally
      toast.success('Settings saved — theme updated');
    } catch (e) { toast.error('Failed to save'); }
    setSaving(false);
  };

  const u = (field, value) => setSettings(p => ({ ...p, [field]: value }));

  const uploadNailLengthPhoto = async (index, file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('index', index.toString());
    try {
      const res = await axios.post(`${API}/settings/nail-length-photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) {
        u('nail_lengths', (settings.nail_lengths || []).map((l, i) => i === index ? { ...l, photo_url: res.data.url } : l));
        toast.success('Photo saved');
      }
    } catch { toast.error('Photo upload failed'); }
  };

  const uploadNailFinishPhoto = async (index, file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('index', index.toString());
    try {
      const res = await axios.post(`${API}/settings/nail-finish-photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) {
        u('nail_finishes', (settings.nail_finishes || []).map((l, i) => i === index ? { ...l, icon_url: res.data.url } : l));
        toast.success('Icon saved');
      }
    } catch { toast.error('Icon upload failed'); }
  };

  const disconnectGoogle = async () => {
    if (!window.confirm('Disconnect Google Calendar? This will stop syncing bookings.')) return;
    try {
      await axios.post(`${API}/oauth/google/disconnect`);
      setGcalStatus({ connected: false });
      setGcalTest(null);
      setGcalCalendars([]);
      toast.success('Google Calendar disconnected');
    } catch (e) { toast.error('Failed to disconnect'); }
  };

  return (
    <div className="space-y-5 fade-in max-w-4xl">
      <Toaster position="top-center" />
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>Settings</h1>
        <Button onClick={save} disabled={saving} className="rounded-xl">
          <Save size={16} className="mr-1" /> {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <Tabs defaultValue="details">
        <TabsList className="rounded-xl">
          <TabsTrigger value="details" className="rounded-lg text-xs"><Building size={13} className="mr-1" /> Salon</TabsTrigger>
          <TabsTrigger value="branding" className="rounded-lg text-xs"><Palette size={13} className="mr-1" /> Branding</TabsTrigger>
          <TabsTrigger value="socials" className="rounded-lg text-xs"><Share2 size={13} className="mr-1" /> Socials</TabsTrigger>
          <TabsTrigger value="policies" className="rounded-lg text-xs"><ShieldCheck size={13} className="mr-1" /> Policies</TabsTrigger>
          <TabsTrigger value="website" className="rounded-lg text-xs"><Globe size={13} className="mr-1" /> Website</TabsTrigger>
          <TabsTrigger value="reviews-mode" className="rounded-lg text-xs" data-testid="settings-tab-reviews"><Star size={13} className="mr-1" /> Reviews & Team</TabsTrigger>
          <TabsTrigger value="integrations" className="rounded-lg text-xs"><ExternalLink size={13} className="mr-1" /> Integrations</TabsTrigger>
        </TabsList>

        {/* Salon Details */}
        <TabsContent value="details" className="mt-4">
          <Card className="p-6 rounded-2xl border shadow-sm">
            <h3 className="text-base font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>Salon Details</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label className="text-xs">Salon / Brand Name</Label><Input value={settings.salon_name || ''} onChange={e => u('salon_name', e.target.value)} className="mt-1 rounded-xl" /></div>
              <div><Label className="text-xs">Tagline</Label><Input value={settings.tagline || ''} onChange={e => u('tagline', e.target.value)} className="mt-1 rounded-xl" placeholder="e.g. NAILS • BROWS • BEAUTY" /></div>
              <div><Label className="text-xs">URL Slug</Label><Input value={settings.salon_slug || ''} onChange={e => u('salon_slug', e.target.value)} className="mt-1 rounded-xl" /></div>
              <div><Label className="text-xs">Email</Label><Input value={settings.email || ''} onChange={e => u('email', e.target.value)} className="mt-1 rounded-xl" /></div>
              <div><Label className="text-xs">Phone</Label><Input value={settings.phone || ''} onChange={e => u('phone', e.target.value)} className="mt-1 rounded-xl" /></div>
              <div><Label className="text-xs">Timezone</Label><Input value={settings.timezone || ''} onChange={e => u('timezone', e.target.value)} className="mt-1 rounded-xl" /></div>
              <div className="md:col-span-2"><Label className="text-xs">Address</Label><Textarea value={settings.address || ''} onChange={e => u('address', e.target.value)} className="mt-1 rounded-xl" rows={3} placeholder={'5 High Street\nLondon\nW1A 1AA, UK'} /></div>
              <div className="md:col-span-2">
                <Label className="text-xs">Location summary</Label>
                <Input value={settings.location_summary || ''} onChange={e => u('location_summary', e.target.value)} className="mt-1 rounded-xl" placeholder="e.g. Conveniently located in Staines-upon-Thames, Surrey" />
                <p className="text-[10px] text-muted-foreground mt-1">One-line caption shown under "Visit us" on the public site.</p>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Google Maps Place ID</Label>
                <Input
                  value={settings.google_place_id || ''}
                  onChange={e => u('google_place_id', e.target.value)}
                  className="mt-1 rounded-xl"
                  placeholder="e.g. ChIJoVmn2BRxdkgRZjjvBm_CflI"
                  data-testid="google-place-id-input"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Find yours at <a href="https://developers.google.com/maps/documentation/javascript/examples/places-placeid-finder" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google's Place ID Finder</a>. Leave blank to use Address for the embedded map.
                </p>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Nearby landmarks</Label>
                <Textarea
                  value={Array.isArray(settings.location_landmarks) ? settings.location_landmarks.join('\n') : (settings.location_landmarks || '')}
                  onChange={e => u('location_landmarks', e.target.value.split('\n').map(s => s.trim()).filter(Boolean))}
                  className="mt-1 rounded-xl"
                  rows={3}
                  placeholder={'5 min walk from station\nClose to High Street'}
                  data-testid="location-landmarks-input"
                />
                <p className="text-[10px] text-muted-foreground mt-1">One landmark per line. Shown as a small "Nearby landmarks" card on Find Us section.</p>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Branding */}
        <TabsContent value="branding" className="mt-4 space-y-4">
          <div className="grid lg:grid-cols-3 gap-4">
            {/* Logo uploads */}
            <Card className="p-5 rounded-2xl border shadow-sm lg:col-span-2">
              <h3 className="text-base font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>Logos</h3>
              <div className="grid sm:grid-cols-2 gap-6">
                <LogoUpload label="Primary Logo" currentUrl={settings.logo_url} fieldName="logo_url"
                  onUploaded={url => u('logo_url', url)} />
                <LogoUpload label="Icon Logo (sidebar/favicon)" currentUrl={settings.icon_logo_url} fieldName="icon_logo_url"
                  onUploaded={url => u('icon_logo_url', url)} />
              </div>
            </Card>

            {/* Live Preview */}
            <Card className="p-5 rounded-2xl border shadow-sm" style={{ background: settings.color_background || '#faf7f6' }}>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-3">Live Preview</p>
              <div className="text-center space-y-3">
                {settings.logo_url && <img src={settings.logo_url} alt="Logo" className="w-16 h-16 rounded-xl mx-auto object-contain" />}
                <div>
                  <p className="text-lg font-semibold" style={{ fontFamily: 'Playfair Display', color: settings.color_text || '#3d3433' }}>
                    {settings.salon_name || 'Salon Name'}
                  </p>
                  {settings.tagline && <p className="text-[9px] tracking-widest" style={{ color: settings.color_accent || '#c9a89c' }}>{settings.tagline}</p>}
                </div>
                <button className="px-4 py-2 rounded-xl text-xs font-medium text-white" style={{ background: settings.color_primary || '#3d3433' }}>
                  Book Now
                </button>
                <p className="text-xs" style={{ color: settings.color_text || '#3d3433' }}>Sample body text</p>
              </div>
            </Card>
          </div>

          {/* Colors */}
          <Card className="p-5 rounded-2xl border shadow-sm">
            <h3 className="text-base font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>Brand Colours</h3>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
              <ColorPicker label="Primary Colour" value={settings.color_primary} onChange={v => u('color_primary', v)} />
              <ColorPicker label="Accent Colour" value={settings.color_accent} onChange={v => u('color_accent', v)} />
              <ColorPicker label="Background Colour" value={settings.color_background} onChange={v => u('color_background', v)} />
              <ColorPicker label="Text / Heading Colour" value={settings.color_text} onChange={v => u('color_text', v)} />
            </div>
            <p className="text-[10px] text-muted-foreground mt-3">These colours are applied to the booking page and email templates. Click "Save Changes" to apply.</p>
          </Card>

          {/* Sharing & SEO */}
          <Card className="p-5 rounded-2xl border shadow-sm" data-testid="seo-share-card">
            <h3 className="text-base font-semibold mb-1" style={{ fontFamily: 'Playfair Display' }}>Sharing & SEO</h3>
            <p className="text-xs text-muted-foreground mb-4">
              Controls how your booking link appears in browser tabs, on Google search
              results, and as the preview card when someone shares your URL on
              WhatsApp, Slack, iMessage or social media.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs">Page Title</Label>
                <Input
                  data-testid="seo-meta-title"
                  value={settings.meta_title || ''}
                  onChange={e => u('meta_title', e.target.value)}
                  className="mt-1 rounded-xl"
                  placeholder={settings.salon_name ? `${settings.salon_name} — Book your appointment` : 'Your salon name — Book your appointment'}
                />
                <p className="text-[10px] text-muted-foreground mt-1">Browser tab title. Keep under ~60 characters for Google.</p>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Page Description</Label>
                <textarea
                  data-testid="seo-meta-description"
                  value={settings.meta_description || ''}
                  onChange={e => u('meta_description', e.target.value)}
                  className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm min-h-[72px] resize-y"
                  placeholder="One or two sentences a stranger reads when they see your link previewed somewhere."
                />
                <p className="text-[10px] text-muted-foreground mt-1">Shown under the title on Google, WhatsApp, etc. Aim for 120–160 characters.</p>
              </div>
              <div>
                <Label className="text-xs">Site Name</Label>
                <Input
                  data-testid="seo-meta-site-name"
                  value={settings.meta_site_name || ''}
                  onChange={e => u('meta_site_name', e.target.value)}
                  className="mt-1 rounded-xl"
                  placeholder={settings.salon_name || 'Your Salon'}
                />
                <p className="text-[10px] text-muted-foreground mt-1">Shown above the title in iMessage/Slack/WhatsApp previews.</p>
              </div>
              <div>
                <Label className="text-xs">Keywords (optional)</Label>
                <Input
                  data-testid="seo-meta-keywords"
                  value={settings.meta_keywords || ''}
                  onChange={e => u('meta_keywords', e.target.value)}
                  className="mt-1 rounded-xl"
                  placeholder="nail salon, gel manicure, biab"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Comma-separated. Most search engines ignore this; safe to leave blank.</p>
              </div>
              <div>
                <Label className="text-xs">Favicon URL</Label>
                <Input
                  data-testid="seo-meta-favicon"
                  value={settings.meta_favicon_url || ''}
                  onChange={e => u('meta_favicon_url', e.target.value)}
                  className="mt-1 rounded-xl"
                  placeholder="https://… (32×32 or 64×64 .png recommended)"
                />
                <p className="text-[10px] text-muted-foreground mt-1">The small icon next to the page title in browser tabs. Leave blank to use your Icon Logo.</p>
              </div>
              <div>
                <Label className="text-xs">Share Image URL</Label>
                <Input
                  data-testid="seo-meta-image"
                  value={settings.meta_image_url || ''}
                  onChange={e => u('meta_image_url', e.target.value)}
                  className="mt-1 rounded-xl"
                  placeholder="https://… (1200×630 .png/.jpg ideal)"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Preview thumbnail shown when your link is shared. Leave blank to use your Primary Logo.</p>
              </div>
            </div>
            <div className="mt-5 p-3 rounded-xl bg-muted/40 border border-border" data-testid="seo-preview">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Preview · iMessage / Slack style</p>
              <div className="flex gap-3 items-stretch">
                {(settings.meta_image_url || settings.logo_url) && (
                  <img
                    src={settings.meta_image_url || settings.logo_url}
                    alt="share preview"
                    className="w-20 h-20 rounded-lg object-cover border border-border shrink-0"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] text-muted-foreground truncate">
                    {settings.meta_site_name || settings.salon_name || 'Your Salon'}
                  </p>
                  <p className="text-sm font-semibold leading-tight truncate" style={{ fontFamily: 'Playfair Display' }}>
                    {settings.meta_title || (settings.salon_name ? `${settings.salon_name} — Book your appointment` : 'Page Title')}
                  </p>
                  <p className="text-xs text-muted-foreground line-clamp-2 mt-1 leading-snug">
                    {settings.meta_description || settings.tagline || 'Page description preview goes here.'}
                  </p>
                </div>
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground mt-3">
              Changes go live as soon as you click "Save Changes". Note: Facebook / WhatsApp
              cache previews aggressively — they may take a day to refresh, or you can
              force a re-scrape with their developer tools.
            </p>
          </Card>

          {/* Shareable link with rich preview has moved to Sharing Tools (sidebar → System → Sharing Tools). */}
        </TabsContent>

        {/* Socials */}
        <TabsContent value="socials" className="mt-4">
          <Card className="p-6 rounded-2xl border shadow-sm">
            <h3 className="text-base font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>Social Links</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label className="text-xs">Instagram</Label><Input value={settings.social_instagram || ''} onChange={e => u('social_instagram', e.target.value)} className="mt-1 rounded-xl" placeholder="https://instagram.com/..." /></div>
              <div><Label className="text-xs">TikTok</Label><Input value={settings.social_tiktok || ''} onChange={e => u('social_tiktok', e.target.value)} className="mt-1 rounded-xl" placeholder="https://tiktok.com/@..." /></div>
              <div><Label className="text-xs">Facebook</Label><Input value={settings.social_facebook || ''} onChange={e => u('social_facebook', e.target.value)} className="mt-1 rounded-xl" placeholder="https://facebook.com/..." /></div>
              <div><Label className="text-xs">WhatsApp</Label><Input value={settings.social_whatsapp || ''} onChange={e => u('social_whatsapp', e.target.value)} className="mt-1 rounded-xl" placeholder="https://wa.me/44..." /></div>
              <div><Label className="text-xs">LinkedIn (optional)</Label><Input value={settings.social_linkedin || ''} onChange={e => u('social_linkedin', e.target.value)} className="mt-1 rounded-xl" placeholder="https://linkedin.com/in/..." /></div>
              <div><Label className="text-xs">Website / Booking Link</Label><Input value={settings.social_website || ''} onChange={e => u('social_website', e.target.value)} className="mt-1 rounded-xl" placeholder="https://..." /></div>
            </div>
            <p className="text-[10px] text-muted-foreground mt-3">These links appear on your booking page footer and social section. Leave blank to hide.</p>
          </Card>
        </TabsContent>

        {/* Policies */}
        <TabsContent value="policies" className="mt-4">
          <Card className="p-6 rounded-2xl border shadow-sm">
            <h3 className="text-base font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>Booking Policies</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label className="text-xs">Currency</Label><Input value={settings.currency || 'GBP'} onChange={e => u('currency', e.target.value)} className="mt-1 rounded-xl" /></div>
              <div><Label className="text-xs">Tax Rate (%)</Label><Input type="number" step="0.1" value={settings.tax_rate || 0} onChange={e => u('tax_rate', parseFloat(e.target.value) || 0)} className="mt-1 rounded-xl" /></div>
              <div><Label className="text-xs">Cancellation Window (hours)</Label><Input type="number" value={settings.cancellation_window_hours || 24} onChange={e => u('cancellation_window_hours', parseInt(e.target.value) || 24)} className="mt-1 rounded-xl" /></div>
              <div><Label className="text-xs">VIP Threshold (completed bookings)</Label><Input type="number" min="1" value={settings.vip_threshold || 10} onChange={e => u('vip_threshold', parseInt(e.target.value) || 10)} className="mt-1 rounded-xl" /><p className="text-[10px] text-muted-foreground mt-1">Clients with this many completed bookings are automatically marked VIP</p></div>
              <div><Label className="text-xs">VIP Reward Interval (visits)</Label><Input type="number" min="1" value={settings.vip_reward_interval || 10} onChange={e => u('vip_reward_interval', parseInt(e.target.value) || 10)} className="mt-1 rounded-xl" /><p className="text-[10px] text-muted-foreground mt-1">VIP clients earn a reward every X completed visits after becoming VIP</p></div>
              <Separator className="my-2 md:col-span-2" />
              <div className="md:col-span-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">VIP Reward Fulfilment</p>
                <div className="flex items-center gap-3 mb-4">
                  <Switch
                    checked={settings.vip_reward_enabled !== false}
                    onCheckedChange={v => u('vip_reward_enabled', v)}
                    data-testid="vip-reward-enabled"
                  />
                  <Label className="text-xs">Auto-issue rewards when a milestone is reached</Label>
                </div>
                {settings.vip_reward_enabled !== false && (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">Reward Type</Label>
                      <select
                        value={settings.vip_reward_type || 'voucher_amount'}
                        onChange={e => u('vip_reward_type', e.target.value)}
                        data-testid="vip-reward-type"
                        className="mt-1 w-full h-10 px-3 rounded-xl border border-input bg-background text-sm"
                      >
                        <option value="voucher_amount">Voucher (£ off)</option>
                        <option value="percent">Percentage off</option>
                        <option value="free_service">Free service</option>
                      </select>
                    </div>
                    {(settings.vip_reward_type || 'voucher_amount') === 'voucher_amount' && (
                      <div>
                        <Label className="text-xs">Voucher Value (£)</Label>
                        <Input
                          type="number" step="0.01" min="0"
                          value={settings.vip_reward_amount ?? 15}
                          onChange={e => u('vip_reward_amount', parseFloat(e.target.value) || 0)}
                          data-testid="vip-reward-amount"
                          className="mt-1 rounded-xl"
                        />
                      </div>
                    )}
                    {settings.vip_reward_type === 'percent' && (
                      <div>
                        <Label className="text-xs">Discount Percentage (%)</Label>
                        <Input
                          type="number" step="1" min="1" max="100"
                          value={settings.vip_reward_amount ?? 20}
                          onChange={e => u('vip_reward_amount', parseInt(e.target.value) || 0)}
                          data-testid="vip-reward-percent"
                          className="mt-1 rounded-xl"
                        />
                      </div>
                    )}
                    {settings.vip_reward_type === 'free_service' && (
                      <div>
                        <Label className="text-xs">Free Service</Label>
                        <select
                          value={settings.vip_reward_free_service_id || ''}
                          onChange={e => u('vip_reward_free_service_id', e.target.value)}
                          data-testid="vip-reward-free-service"
                          className="mt-1 w-full h-10 px-3 rounded-xl border border-input bg-background text-sm"
                        >
                          <option value="">Cheapest service in their basket</option>
                          {services.map(s => (
                            <option key={s.id} value={s.id}>{s.name} (£{s.price})</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div>
                      <Label className="text-xs">Reward Expiry (days)</Label>
                      <Input
                        type="number" min="1" max="3650"
                        value={settings.vip_reward_expiry_days ?? 90}
                        onChange={e => u('vip_reward_expiry_days', parseInt(e.target.value) || 90)}
                        data-testid="vip-reward-expiry-days"
                        className="mt-1 rounded-xl"
                      />
                      <p className="text-[10px] text-muted-foreground mt-1">How long clients have to redeem before the reward expires</p>
                    </div>
                  </div>
                )}
                <p className="text-[10px] text-muted-foreground mt-3">Rewards are emailed to the client and auto-applied at their next checkout.</p>
              </div>
              <Separator className="my-2 md:col-span-2" />
              <div className="md:col-span-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Win-back Email (Lapsed Clients)</p>
                <div className="grid md:grid-cols-3 gap-4 mb-3">
                  <div>
                    <Label className="text-xs">Voucher Amount (£)</Label>
                    <Input
                      type="number" step="0.01" min="0"
                      value={settings.winback_voucher_amount ?? 10}
                      onChange={e => u('winback_voucher_amount', parseFloat(e.target.value) || 0)}
                      data-testid="winback-amount"
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Voucher Expiry (days)</Label>
                    <Input
                      type="number" min="1" max="3650"
                      value={settings.winback_voucher_expiry_days ?? 60}
                      onChange={e => u('winback_voucher_expiry_days', parseInt(e.target.value) || 60)}
                      data-testid="winback-expiry-days"
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Cooldown (days)</Label>
                    <Input
                      type="number" min="1" max="365"
                      value={settings.winback_cooldown_days ?? 30}
                      onChange={e => u('winback_cooldown_days', parseInt(e.target.value) || 30)}
                      data-testid="winback-cooldown-days"
                      className="mt-1 rounded-xl"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">Min days before re-sending</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Email Subject</Label>
                    <Input
                      value={settings.winback_email_subject || ''}
                      onChange={e => u('winback_email_subject', e.target.value)}
                      data-testid="winback-subject"
                      placeholder="We miss you, {firstName} — here's £{amount} on us"
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Email Body</Label>
                    <Textarea
                      value={settings.winback_email_body || ''}
                      onChange={e => u('winback_email_body', e.target.value)}
                      data-testid="winback-body"
                      rows={6}
                      placeholder="Hi {firstName}, ..."
                      className="mt-1 rounded-xl"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Placeholders: <code>{'{firstName}'}</code>, <code>{'{amount}'}</code>, <code>{'{voucherCode}'}</code>, <code>{'{expiryDate}'}</code>, <code>{'{salonName}'}</code>
                    </p>
                  </div>
                </div>
              </div>
              <Separator className="my-2 md:col-span-2" />
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">VIP Messaging (Booking Page)</p>
              <div><Label className="text-xs">Pre-VIP Headline</Label><Input value={settings.vip_pre_headline || '{remaining} more visits until VIP'} onChange={e => u('vip_pre_headline', e.target.value)} className="mt-1 rounded-xl" /><p className="text-[10px] text-muted-foreground mt-1">Use {'{remaining}'} for visits count</p></div>
              <div><Label className="text-xs">Pre-VIP Subtext</Label><Input value={settings.vip_pre_subtext || 'VIP clients get rewards every {reward_interval} visits + exclusive perks'} onChange={e => u('vip_pre_subtext', e.target.value)} className="mt-1 rounded-xl" /><p className="text-[10px] text-muted-foreground mt-1">Use {'{reward_interval}'} for reward interval</p></div>
              <div><Label className="text-xs">VIP Headline</Label><Input value={settings.vip_headline || "You're a VIP client"} onChange={e => u('vip_headline', e.target.value)} className="mt-1 rounded-xl" /></div>
              <div><Label className="text-xs">VIP Subtext</Label><Input value={settings.vip_subtext || '{remaining} visits until your next reward'} onChange={e => u('vip_subtext', e.target.value)} className="mt-1 rounded-xl" /><p className="text-[10px] text-muted-foreground mt-1">Use {'{remaining}'} for visits until next reward</p></div>
              <div className="flex items-center gap-3 pt-6"><Switch checked={settings.deposit_required || false} onCheckedChange={v => u('deposit_required', v)} /><Label className="text-xs">Require deposit</Label></div>
              {settings.deposit_required && (
                <div><Label className="text-xs">Deposit Amount (£)</Label><Input type="number" step="0.01" value={settings.deposit_amount || 0} onChange={e => u('deposit_amount', parseFloat(e.target.value) || 0)} className="mt-1 rounded-xl" /></div>
              )}
              <div className="flex items-center gap-3 pt-6"><Switch checked={settings.booking_approval_required || false} onCheckedChange={v => u('booking_approval_required', v)} /><Label className="text-xs">Require booking approval</Label></div>

              {/* New Client Discount */}
              <div className="pt-6 border-t mt-4">
                <div className="flex items-center gap-3 mb-3">
                  <Switch
                    checked={settings.new_client_promo_enabled ?? true}
                    onCheckedChange={v => u('new_client_promo_enabled', v)}
                    data-testid="new-client-promo-toggle"
                  />
                  <div>
                    <Label className="text-xs font-medium">New Client Discount</Label>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Show a welcome discount to first-time bookers on the booking page</p>
                  </div>
                </div>
                {(settings.new_client_promo_enabled ?? true) && (
                  <div className="pl-1">
                    <Label className="text-xs">Discount Amount (£)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm text-muted-foreground">£</span>
                      <Input
                        type="number" step="1" min="0"
                        value={settings.new_client_promo_amount ?? 5}
                        onChange={e => u('new_client_promo_amount', parseFloat(e.target.value) || 0)}
                        className="rounded-xl w-28"
                        data-testid="new-client-promo-amount"
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">Shown as a badge on Step 3 of the booking wizard when the email isn't recognised</p>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Category Order */}
          <Card className="p-6 rounded-2xl border shadow-sm mt-4">
            <h3 className="text-base font-semibold mb-1" style={{ fontFamily: 'Playfair Display' }}>Service Category Order</h3>
            <p className="text-xs text-muted-foreground mb-4">Control which category appears first on the public booking page. Use ↑ ↓ to reorder.</p>
            <CategoryOrderEditor
              value={settings.category_order || []}
              onChange={order => u('category_order', order)}
            />
          </Card>

          {/* Nail Lengths Master List */}
          <Card className="p-6 rounded-2xl border shadow-sm mt-4">
            <h3 className="text-base font-semibold mb-1" style={{ fontFamily: 'Playfair Display' }}>Nail Length Pricing</h3>
            <p className="text-xs text-muted-foreground mb-4">
              Define your global nail length options and price uplifts. Services with "Nail Length Pricing" enabled will use this list.
              Upload a photo for each length — it will appear under the selection button on the booking page.
            </p>
            <div className="space-y-2">
              {(settings.nail_lengths || []).map((entry, i) => (
                <NailLengthRow
                  key={i}
                  entry={entry}
                  index={i}
                  onChange={updated => u('nail_lengths', (settings.nail_lengths || []).map((l, j) => j === i ? updated : l))}
                  onDelete={() => u('nail_lengths', (settings.nail_lengths || []).filter((_, j) => j !== i))}
                  onPhotoUpload={uploadNailLengthPhoto}
                />
              ))}
            </div>
            <Button
              variant="outline" size="sm"
              className="rounded-xl mt-3 text-xs"
              onClick={() => u('nail_lengths', [...(settings.nail_lengths || []), { name: '', price_uplift: 0, photo_url: '' }])}
            >
              <Plus size={12} className="mr-1" /> Add length
            </Button>
            <p className="text-[10px] text-muted-foreground mt-3">Click "Save Changes" at the top to apply. Photos are saved immediately on upload.</p>
          </Card>

          {/* Nail Finish / Add-ons Master List */}
          <Card className="p-6 rounded-2xl border shadow-sm mt-4" data-testid="nail-finishes-card">
            <h3 className="text-base font-semibold mb-1" style={{ fontFamily: 'Playfair Display' }}>Finish / Add-ons</h3>
            <p className="text-xs text-muted-foreground mb-4">
              Optional finishes (French, Gel Colour, Glitter, Nail Art, Chrome, Nail Fix…) shown on the booking page beneath the nail-length picker
              for any service with "Nail Length Pricing" enabled. Customers can select one or more, or skip entirely. Toggle off entries you want to hide
              without deleting them.
            </p>
            <div className="space-y-2">
              {(settings.nail_finishes || []).map((entry, i) => (
                <NailFinishRow
                  key={i}
                  entry={entry}
                  index={i}
                  onChange={updated => u('nail_finishes', (settings.nail_finishes || []).map((l, j) => j === i ? updated : l))}
                  onDelete={() => u('nail_finishes', (settings.nail_finishes || []).filter((_, j) => j !== i))}
                  onPhotoUpload={uploadNailFinishPhoto}
                />
              ))}
            </div>
            <Button
              variant="outline" size="sm"
              className="rounded-xl mt-3 text-xs"
              data-testid="add-nail-finish-btn"
              onClick={() => u('nail_finishes', [...(settings.nail_finishes || []), { name: '', description: '', price: 0, icon_url: '', active: true }])}
            >
              <Plus size={12} className="mr-1" /> Add finish / extra
            </Button>
            <p className="text-[10px] text-muted-foreground mt-3">Click "Save Changes" at the top to apply. Icons are saved immediately on upload.</p>
          </Card>

          {/* Email Communications */}
          <Card className="p-6 rounded-2xl border shadow-sm mt-4" data-testid="email-comms-card">
            <h3 className="text-base font-semibold mb-1" style={{ fontFamily: 'Playfair Display' }}>Email Communications</h3>
            <p className="text-xs text-muted-foreground mb-4">
              These details appear in every booking confirmation, cancellation and reschedule email sent to your customers.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Sender email (`From` address)</Label>
                <Input
                  value={settings.email_from || ''}
                  onChange={e => u('email_from', e.target.value)}
                  className="mt-1 rounded-xl font-mono text-xs"
                  placeholder={'Salon Name <booking@yourdomain.com>'}
                  data-testid="settings-email-from"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Must be a domain you've verified in Resend.</p>
              </div>
              <div>
                <Label className="text-xs">Reply-to email</Label>
                <Input
                  value={settings.email_reply_to || ''}
                  onChange={e => u('email_reply_to', e.target.value)}
                  className="mt-1 rounded-xl font-mono text-xs"
                  placeholder="you@yoursalon.com"
                  data-testid="settings-email-reply-to"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Where customer replies land + where the owner-copy of each booking is sent.</p>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Banking details (for outstanding-balance emails)</Label>
                <Textarea
                  value={settings.banking_details || ''}
                  onChange={e => u('banking_details', e.target.value)}
                  className="mt-1 rounded-xl font-mono text-xs min-h-[110px]"
                  placeholder={'Name: Your Salon\nBank: Your Bank\nAccount number: 12345678\nSort code: 00-00-00'}
                  data-testid="settings-banking-details"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Shown in the confirmation email when a balance is still due. Each line renders separately.</p>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Salon guidelines</Label>
                <Textarea
                  value={settings.email_guidelines || ''}
                  onChange={e => u('email_guidelines', e.target.value)}
                  className="mt-1 rounded-xl text-xs min-h-[160px]"
                  placeholder={'Please arrive on time.\nA 24-hour cancellation notice is required.\n...'}
                  data-testid="settings-email-guidelines"
                />
                <p className="text-[10px] text-muted-foreground mt-1">One rule per line — rendered as a bulleted list in the confirmation email.</p>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Footer tagline</Label>
                <Input
                  value={settings.email_footer_text || ''}
                  onChange={e => u('email_footer_text', e.target.value)}
                  className="mt-1 rounded-xl text-xs"
                  placeholder={`${settings.salon_name || 'Your Salon'} · Powered by B∞kd`}
                  data-testid="settings-email-footer"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Bottom-of-email signature. Leave blank to use the default "Salon Name · Powered by B∞kd".</p>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* ── Reviews & Team mode ── */}
        <TabsContent value="reviews-mode" className="mt-4 space-y-4" data-testid="reviews-mode-tab">
          <Card className="p-6 rounded-2xl border shadow-sm">
            <h3 className="text-base font-semibold mb-1 flex items-center gap-2" style={{ fontFamily: 'Playfair Display' }}>
              <Star size={16} className="text-primary" /> Review source
            </h3>
            <p className="text-xs text-muted-foreground mb-4">
              Choose whether this salon collects and displays reviews directly through B∞kd, from Google, or both.
            </p>
            <RadioGroup
              value={settings.review_mode || 'google'}
              onValueChange={v => u('review_mode', v)}
              className="space-y-3"
            >
              <label className="flex items-start gap-3 p-3 rounded-xl border border-border hover:bg-muted/40 cursor-pointer" data-testid="review-mode-direct">
                <RadioGroupItem value="direct" id="review-direct" className="mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Direct website reviews</p>
                  <p className="text-xs text-muted-foreground">Native B∞kd verified reviews only. Hides Google widgets.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-xl border border-border hover:bg-muted/40 cursor-pointer" data-testid="review-mode-google">
                <RadioGroupItem value="google" id="review-google" className="mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Google reviews only</p>
                  <p className="text-xs text-muted-foreground">Show Google review widget. Hide direct review form/page.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-xl border border-border hover:bg-muted/40 cursor-pointer" data-testid="review-mode-both">
                <RadioGroupItem value="both" id="review-both" className="mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Both direct and Google reviews</p>
                  <p className="text-xs text-muted-foreground">Show both on the public site. Both sources are kept separate.</p>
                </div>
              </label>
            </RadioGroup>
            <Separator className="my-5" />
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium">Auto-approve new direct reviews</p>
                <p className="text-xs text-muted-foreground">Off = new submissions stay hidden until you approve them.</p>
              </div>
              <Switch
                checked={settings.reviews_auto_approve !== false}
                onCheckedChange={v => u('reviews_auto_approve', v)}
                data-testid="reviews-auto-approve-toggle"
              />
            </div>
          </Card>

          <Card className="p-6 rounded-2xl border shadow-sm">
            <h3 className="text-base font-semibold mb-1 flex items-center gap-2" style={{ fontFamily: 'Playfair Display' }}>
              <Users size={16} className="text-primary" /> Business / staff setup
            </h3>
            <p className="text-xs text-muted-foreground mb-4">
              Use <strong>Single operator</strong> when the salon has one main bookable person. Use <strong>Team</strong> when reporting and targets should be tracked per staff member.
            </p>
            <RadioGroup
              value={settings.business_mode || 'single'}
              onValueChange={v => u('business_mode', v)}
              className="space-y-3"
            >
              <label className="flex items-start gap-3 p-3 rounded-xl border border-border hover:bg-muted/40 cursor-pointer" data-testid="business-mode-single">
                <RadioGroupItem value="single" id="biz-single" className="mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Single operator</p>
                  <p className="text-xs text-muted-foreground">One overall monthly target. Reports show salon-level totals.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-xl border border-border hover:bg-muted/40 cursor-pointer" data-testid="business-mode-team">
                <RadioGroupItem value="team" id="biz-team" className="mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Team</p>
                  <p className="text-xs text-muted-foreground">Per-staff targets, per-staff revenue breakdown, and reviews can be filtered by team member.</p>
                </div>
              </label>
            </RadioGroup>
          </Card>
        </TabsContent>

        {/* Integrations */}
        <TabsContent value="integrations" className="mt-4">
          <IntegrationsTab
            gcalStatus={gcalStatus}
            gcalTesting={gcalTesting}
            gcalTest={gcalTest}
            gcalCalendars={gcalCalendars}
            syncCalendarId={syncCalendarId}
            setSyncCalendarId={setSyncCalendarId}
            busyCalendarIds={busyCalendarIds}
            toggleBusyCalendar={toggleBusyCalendar}
            savingCalSettings={savingCalSettings}
            saveCalendarSettings={saveCalendarSettings}
            loadCalendars={loadCalendars}
            testGoogleConnection={testGoogleConnection}
            disconnectGoogle={disconnectGoogle}
            settings={settings}
            onSettingsChange={u}
          />
        </TabsContent>
        {/* ── Website Content ── */}
        <TabsContent value="website" className="mt-4 space-y-4">
          <WebsiteTab settings={settings} u={u} />
        </TabsContent>

      </Tabs>
    </div>
  );
}
